import { useCallback, useEffect, useRef, useState } from 'react';

import { type IResultAnalyticsData, type IResultAnalyticsDataResponse } from '../../../../interface/state/IElectionState';
import { getResultAnalyticsByScope } from '../../../../lib/result-analytics-api';
import AnalyticsLevelView from '../../overview/quick-analytics/AnalyticsLevelView';
import { childEntityLabel, type IChildScope } from '../../overview/quick-analytics/utils';

interface IChildrenTabProps {
	// Scope for the entity's children (one rung below the drawer's entity).
	childScope: IChildScope;
	electionId?: number;
	// Open a child's own Agent Coverage drawer (stacked, with Back).
	onOpenChild: (item: IResultAnalyticsData) => void;
	// Bubble the child count up for the tab hint (once known).
	onCount?: (count: number | null) => void;
	// The drawer entity — used as the Tree view's root node.
	rootLabel?: string;
	rootLevelLabel?: string;
}

type LoadState = 'loading' | 'ready' | 'error';

/**
 * The Agent Coverage drawer's "children" tab. Reuses the full analytics card
 * (progress ring, toolbar, coverage badges) scoped to the drawer entity's children,
 * fetched into isolated local state via the standalone client. Tapping a child row
 * opens that child's drawer stacked on top (recursion down to polling units).
 */
const ChildrenTab = ({ childScope, electionId, onOpenChild, onCount, rootLabel, rootLevelLabel }: IChildrenTabProps) => {
	const [data, setData] = useState<IResultAnalyticsDataResponse | undefined>(undefined);
	const [state, setState] = useState<LoadState>('loading');
	const [page, setPage] = useState(1);
	const reqRef = useRef(0);

	const fetchPage = useCallback(
		(p: number) => {
			if (!electionId) {
				setState('error');
				return;
			}
			const reqId = reqRef.current + 1;
			reqRef.current = reqId;
			setState('loading');
			getResultAnalyticsByScope({
				electionId,
				resultType: childScope.resultType,
				...childScope.scope,
				pageNumber: p,
			}).then((outcome) => {
				if (reqId !== reqRef.current) return; // superseded
				if (!outcome.reachable || outcome.httpStatus < 200 || outcome.httpStatus >= 300 || !outcome.body) {
					setState('error');
					onCount?.(null);
					return;
				}
				setData(outcome.body);
				setPage(p);
				setState('ready');
				onCount?.(outcome.body.statistics?.total ?? outcome.body.data?.length ?? null);
			});
		},
		[electionId, childScope, onCount]
	);

	useEffect(() => {
		fetchPage(1);
	}, [fetchPage]);

	return (
		<AnalyticsLevelView
			data={data}
			load={state === 'loading'}
			error={state === 'error'}
			entityLabel={childEntityLabel(childScope.resultType)}
			electionId={electionId}
			onOpenCoverage={onOpenChild}
			onPage={fetchPage}
			onRetry={() => fetchPage(page)}
			ariaLabel={`${childEntityLabel(childScope.resultType)} under this entity`}
			rootLabel={rootLabel}
			rootLevelLabel={rootLevelLabel}
		/>
	);
};

export default ChildrenTab;
